import {
  ENGAGEMENT_SHAPES,
  type EngagementShape,
  type RequestPanel,
  SIZE_TIERS,
  type SizeTier,
} from "@procuremate/shared";
import { type FormEvent, type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ApiRequestError } from "@/lib/api-client";
import { useSetClassification } from "./api";

/**
 * The requester sets what kind of purchase this is and how big (WP-2.6). Plain-language
 * descriptions from PROCUREMENT_DOMAIN §3; the choice decides which details are mandatory,
 * and the buyer sees that the requester set it.
 */
export function ClassificationEditor({ panel, onClose }: { panel: RequestPanel; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSetClassification(panel.id);
  const [shape, setShape] = useState<EngagementShape | null>(panel.engagementShape);
  const [tier, setTier] = useState<SizeTier | null>(panel.sizeTier);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  const name = useId();
  const errorId = useId();

  useEffect(() => first.current?.focus(), []);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (save.isPending) return;
    if (!shape || !tier) {
      setError("incomplete");
      return;
    }
    save.mutate(
      { engagementShape: shape, sizeTier: tier },
      {
        onSuccess: onClose,
        onError: (err) => setError(err instanceof ApiRequestError && err.status === 409 ? "busy" : "generic"),
      },
    );
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  }

  const option =
    "flex items-start gap-2.5 rounded-md border border-border p-2.5 has-[:checked]:border-primary has-[:checked]:bg-bg";

  return (
    <form className="mt-3 flex flex-col gap-4" onSubmit={submit} onKeyDown={onKeyDown} noValidate>
      <fieldset aria-describedby={error ? errorId : undefined}>
        <legend className="mb-2 text-sm font-semibold text-fg">{t("requests.classification.shapeQuestion")}</legend>
        <div className="flex flex-col gap-2">
          {ENGAGEMENT_SHAPES.map((s, i) => (
            <label key={s} className={option}>
              <input
                ref={i === 0 ? first : undefined}
                type="radio"
                name={`${name}-shape`}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
                checked={shape === s}
                onChange={() => {
                  setShape(s);
                  setError(null);
                }}
              />
              <span>
                <span className="block text-sm font-semibold text-fg">{t(`requests.panel.shapes.${s}`)}</span>
                <span className="block text-sm text-fg-muted">{t(`requests.classification.shapeHelp.${s}`)}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset aria-describedby={error ? errorId : undefined}>
        <legend className="mb-2 text-sm font-semibold text-fg">{t("requests.classification.tierQuestion")}</legend>
        <div className="flex flex-col gap-2">
          {SIZE_TIERS.map((s) => (
            <label key={s} className={option}>
              <input
                type="radio"
                name={`${name}-tier`}
                className="mt-1 h-4 w-4 shrink-0 accent-primary"
                checked={tier === s}
                onChange={() => {
                  setTier(s);
                  setError(null);
                }}
              />
              <span>
                <span className="block text-sm font-semibold text-fg">
                  {t(`requests.classification.tierName.${s}`)}
                </span>
                <span className="block text-sm text-fg-muted">{t(`requests.classification.tierHelp.${s}`)}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <p className="text-sm text-fg-muted">{t("requests.classification.note")}</p>
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-danger">
          {t(`requests.classification.errors.${error}`)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" className="h-9 px-3" aria-disabled={save.isPending || undefined}>
          {save.isPending ? t("requests.panel.saving") : t("requests.panel.save")}
        </Button>
        <Button variant="secondary" className="h-9 px-3" onClick={onClose}>
          {t("requests.panel.cancel")}
        </Button>
      </div>
    </form>
  );
}
